import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Checkbox } from '@/ui'
import { TextField } from '@/ui/form/controls/TextField'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import {
  DAY_NOTES_MAX_LENGTH,
  DriverDayInfo,
  saveDriverDayInfo,
} from '@/lib/api/company/driverDay/api'
import {
  driverDayInfoKey,
  useQueryDriverDayInfo,
} from '@/lib/api/company/driverDay/useQueryDriverDayInfo'

// Widoczny tylko, gdy dzień ma notatkę albo oznaczone 7.3; pusty dzień
// uzupełnia się pod ikoną w pasku akcji (DayInfoButton).
const DayInfoPanel = ({
  companyId,
  driverId,
  date,
}: {
  companyId: string
  driverId: number
  /** YYYY-MM-DD */
  date: string
}) => {
  const { t } = useLocalize()
  const can = usePermissionChecker(companyId)
  const { triggerRefresh } = useActivity()
  const queryClient = useQueryClient()
  const day = { companyId, driverId, date }
  const key = driverDayInfoKey(day)
  const { data: saved } = useQueryDriverDayInfo(day)
  const [notes, setNotes] = React.useState('')
  // Zapis flagi 7.3 przelicza naruszenia.
  const [busy, setBusy] = React.useState(false)
  const wanted = React.useRef<DriverDayInfo | null>(null)
  const queue = React.useRef<Promise<void>>(Promise.resolve())

  React.useEffect(() => {
    wanted.current = saved ?? null
    setNotes(saved?.notes ?? '')
  }, [saved])

  if (!saved || (!saved.notes && !saved.switchViolation)) return null

  // Zapisy idą po kolei, żeby klik w checkbox tuż po edycji notatki nie ginął.
  const save = (change: Partial<DriverDayInfo>) => {
    const next = { ...(wanted.current ?? saved), ...change }
    wanted.current = next
    const flagChange = next.switchViolation !== saved.switchViolation
    if (flagChange) setBusy(true)

    queue.current = queue.current.then(async () => {
      const before = queryClient.getQueryData<DriverDayInfo>(key)
      try {
        const { data } = await saveDriverDayInfo({ ...day, ...next })
        queryClient.setQueryData(key, data)
        if (data.switchViolation !== before?.switchViolation) triggerRefresh()
      } catch (error) {
        console.error('DayInfoPanel: save failed', error)
        wanted.current = queryClient.getQueryData<DriverDayInfo>(key) ?? null
        toast.error({ text: t('driverModule.day-info-save-error') })
      } finally {
        if (flagChange) setBusy(false)
      }
    })
  }

  const saveNotes = () => {
    const trimmed = notes.trim()
    if (trimmed === (wanted.current?.notes ?? '')) return
    save({ notes: trimmed || null })
  }

  const disabled = busy || !can('canEditActivityDay')
  const checkboxId = `day-switch-violation-inline-${driverId}-${date}`

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2 tw-border-t tw-border-base-200 tw-px-4 tw-py-2">
      <div className="tw-flex tw-min-w-[280px] tw-flex-1 tw-items-center tw-gap-2">
        <span className="tw-text-sm tw-font-medium">
          {t('driverModule.day-info')}:
        </span>
        <TextField
          fullWidth
          aria-label={t('driverModule.day-info')}
          maxLength={DAY_NOTES_MAX_LENGTH}
          value={notes}
          disabled={disabled}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={saveNotes}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          }}
        />
      </div>
      <label
        htmlFor={checkboxId}
        className="tw-m-0 tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm"
      >
        <Checkbox
          id={checkboxId}
          checked={saved.switchViolation}
          disabled={disabled}
          onChange={(e) =>
            save({
              notes: notes.trim() || null,
              switchViolation: e.target.checked,
            })
          }
        />
        {t('chartImport.v73')}
      </label>
    </div>
  )
}

export default DayInfoPanel
